import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';

import useMediaQuery from 'hooks/useMediaQuery';
import useOffersFilters from 'hooks/useOffersFilters';
import useQueryClient from 'hooks/useQueryClient';

import {
  isInternationalOfferByOfferTags,
  isOfferCategoryIdFilterActive,
  isOfferSubcategoryIdFilterActive,
} from 'lib/offer';
import placeholder from 'lib/placeholder';
import { offerQueryKeys } from 'lib/queryKeys';
import { removeHashFromUrl } from 'lib/request';
import { scrollToElement } from 'lib/utils';

import { useGlobalQueries } from 'providers/GlobalQueriesProvider';

import OffersList from './OffersList';
import OffersListHeader from './OffersListHeader';

import OFFER from 'constants/offer';

const ADDITIONAL_SPACE_FROM_TOP_ON_SCROLL_IN_PX = 24;
const MILLISECONDS_TO_WAIT_BEFORE_SCROLLING_TO_LAST_OFFERS = 500;

export const OffersListSection = ({
  cardsAds,
  gaEventCategory,
  landscapeAds,
  serverOffers,
}) => {
  const offersListHeaderRef = useRef(null);
  const hasProcessedHashRef = useRef(false);
  const router = useRouter();
  const { isLg } = useMediaQuery();
  const queryClient = useQueryClient();
  const { activeOffersListTab } = useGlobalQueries();
  const { offersFiltersState, getOffersFilters } = useOffersFilters();

  /**
   * Scroll to last offers section of the page if a valid hash is present
   * at the url
   */
  useEffect(() => {
    const handleHashChange = () => {
      const hasHash = window.location.hash.includes(OFFER.LAST_OFFERS_HASH);

      if (hasHash && !hasProcessedHashRef.current) {
        hasProcessedHashRef.current = true;

        setTimeout(() => {
          scrollToElement(
            offersListHeaderRef.current?.offsetTop -
              ADDITIONAL_SPACE_FROM_TOP_ON_SCROLL_IN_PX,
            isLg
          );
        }, MILLISECONDS_TO_WAIT_BEFORE_SCROLLING_TO_LAST_OFFERS);

        removeHashFromUrl(router);
      }
    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      hasProcessedHashRef.current = false;
    };
  }, [isLg, router]);

  useEffect(() => {
    const refetchOfferPagesWithFilters = async () => {
      const {
        categories,
        subcategories,
        lastAction,
        isInternationalOffersActive,
      } = getOffersFilters();

      const isOfferWithFilterActive = ({
        categoryId,
        subcategoryId,
        offerTags,
      }) =>
        isOfferCategoryIdFilterActive(categories, categoryId) ||
        isOfferSubcategoryIdFilterActive(subcategories, subcategoryId) ||
        (isInternationalOfferByOfferTags(offerTags) &&
          !isInternationalOffersActive);

      // TODO: this can be improved with a future refactor
      if (lastAction === 'ADD' || !lastAction) {
        queryClient.setInfiniteQueryData(
          offerQueryKeys.list({ activeTab: activeOffersListTab.id }),
          (pages) =>
            pages.map((page) => ({
              ...page,
              offers: page.offers.filter(
                (offer) => !isOfferWithFilterActive(offer)
              ),
            }))
        );
        return;
      }

      if (lastAction === 'DELETE') {
        queryClient.refetchQueries(
          offerQueryKeys.list({ activeTab: activeOffersListTab.id })
        );
      }
    };

    refetchOfferPagesWithFilters();
  }, [queryClient, offersFiltersState]);

  return (
    <div className="col-span-4 w-full flex-col md:col-span-12 xl:col-span-12">
      <section className="col-span-4 flex flex-col gap-y-4 md:col-span-12">
        <OffersListHeader
          ref={offersListHeaderRef}
          gaEventCategory={gaEventCategory}
        />
        <ErrorBoundary
          fallback={
            <GenericError message={placeholder('errors.couldNotLoadOffers')} />
          }
        >
          <OffersList
            cardsAds={cardsAds}
            gaEventCategory={gaEventCategory}
            landscapeAds={landscapeAds}
            offersFilters={offersFiltersState}
            offersListHeaderRef={offersListHeaderRef}
            serverOffers={serverOffers}
          />
        </ErrorBoundary>
      </section>
    </div>
  );
};

OffersListSection.propTypes = {
  cardsAds: PropTypes.arrayOf(
    PropTypes.shape({
      bgcolor: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      isClosable: PropTypes.bool.isRequired,
      mobileImage: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      storeSlug: PropTypes.string,
      type: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  landscapeAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string.isRequired,
    })
  ),
  serverOffers: PropTypes.shape({
    after: PropTypes.string.isRequired,
    before: PropTypes.number,
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        categoryId: PropTypes.number,
        categoryName: PropTypes.string,
        categorySlug: PropTypes.string,
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeId: PropTypes.number,
        storeImage: PropTypes.string,
        storeName: PropTypes.string,
        subcategoryId: PropTypes.number,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string,
        userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userTypeName: PropTypes.string,
        userUsername: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
};

export default OffersListSection;
